import React, { useEffect, useState } from 'react';
import { motion, useMotionValue, useTransform, animate } from 'framer-motion';
import type { FocusObjectiveSummary } from './types';
import { MEL_TEAL, MEL_TEAL_DARK } from './types';

function AnimatedNumber({ value, duration = 1.2 }: { value: number; duration?: number }) {
  const mv = useMotionValue(0);
  const rounded = useTransform(mv, (v) => Math.round(v * 10) / 10);
  const [display, setDisplay] = useState('0');

  useEffect(() => {
    const controls = animate(mv, value, { duration, ease: 'easeOut' });
    const unsub = rounded.on('change', (v) => setDisplay(String(v)));
    return () => {
      controls.stop();
      unsub();
    };
  }, [value, duration, mv, rounded]);

  return <span>{display}</span>;
}

function RadialMeter({
  pct,
  label,
  active,
  onClick,
  delay = 0,
}: {
  pct: number | null;
  label: string;
  active: boolean;
  onClick: () => void;
  delay?: number;
}) {
  const size = 88;
  const stroke = 7;
  const r = (size - stroke) / 2;
  const c = 2 * Math.PI * r;
  const safe = pct == null ? 0 : Math.max(0, Math.min(100, pct));
  const offset = c - (safe / 100) * c;

  const short =
    label.includes('PROTECTION') || label.toLowerCase().includes('protection')
      ? 'Protect'
      : label.includes('RESPOND') || label.toLowerCase().includes('crises')
        ? 'Respond'
        : label.includes('CONTROL') || label.toLowerCase().includes('control')
          ? 'Control'
          : label.slice(0, 18);

  return (
    <motion.button
      type="button"
      initial={{ opacity: 0, y: 12 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay, duration: 0.45 }}
      onClick={onClick}
      className={`relative flex flex-col items-center gap-1 rounded-xl px-2 py-2 transition-shadow focus:outline-none focus-visible:ring-2 focus-visible:ring-[#15736d] ${
        active ? 'bg-white shadow-md ring-1 ring-[#15736d]/40' : 'bg-white/70 hover:bg-white hover:shadow-sm'
      }`}
      aria-pressed={active}
    >
      <div className="relative">
        <svg width={size} height={size} className="-rotate-90">
          <circle
            cx={size / 2}
            cy={size / 2}
            r={r}
            fill="none"
            stroke="#e5e7eb"
            strokeWidth={stroke}
          />
          <motion.circle
            cx={size / 2}
            cy={size / 2}
            r={r}
            fill="none"
            stroke={active ? MEL_TEAL_DARK : MEL_TEAL}
            strokeWidth={stroke}
            strokeLinecap="round"
            strokeDasharray={c}
            initial={{ strokeDashoffset: c }}
            animate={{ strokeDashoffset: offset }}
            transition={{ delay: delay + 0.15, duration: 1.1, ease: 'easeOut' }}
          />
        </svg>
        <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
          <div className="text-sm font-bold text-gray-800">
            {pct == null ? '—' : <><AnimatedNumber value={safe} />%</>}
          </div>
        </div>
      </div>
      <span className="text-[11px] font-semibold text-gray-700 max-w-[96px] leading-tight text-center">
        {short}
      </span>
    </motion.button>
  );
}

interface AchievementHudProps {
  overallPct: number | null;
  targetCount: number;
  targetsWithData: number;
  focusObjectives: FocusObjectiveSummary[];
  selectedFocusId: number | null;
  onSelectFocus: (id: number | null) => void;
}

const AchievementHud: React.FC<AchievementHudProps> = ({
  overallPct,
  targetCount,
  targetsWithData,
  focusObjectives,
  selectedFocusId,
  onSelectFocus,
}) => {
  const safeOverall = overallPct == null ? 0 : Math.max(0, Math.min(100, overallPct));
  const arcR = 54;
  const arcC = Math.PI * arcR; // semicircle
  const arcOffset = arcC - (safeOverall / 100) * arcC;

  return (
    <div className="relative rounded-2xl border border-[#c5d9d6] bg-gradient-to-br from-[#f4faf9] via-white to-[#eef6f4] px-4 py-4 shadow-sm overflow-hidden">
      <div
        className="pointer-events-none absolute -right-8 -top-10 h-40 w-40 rounded-full opacity-30"
        style={{ background: `radial-gradient(circle, ${MEL_TEAL}55, transparent 70%)` }}
      />
      <div className="relative flex flex-col lg:flex-row lg:items-center gap-4 lg:gap-8">
        <motion.div
          initial={{ opacity: 0, scale: 0.92 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.5 }}
          className="flex items-center gap-4 min-w-[200px]"
        >
          <div className="flex flex-col items-center">
            <div className="relative w-[140px] h-[80px]">
              <svg width={140} height={80} viewBox="0 0 140 80" className="block">
                <path
                  d={`M 16 70 A ${arcR} ${arcR} 0 0 1 124 70`}
                  fill="none"
                  stroke="#e5e7eb"
                  strokeWidth={10}
                  strokeLinecap="round"
                />
                <motion.path
                  d={`M 16 70 A ${arcR} ${arcR} 0 0 1 124 70`}
                  fill="none"
                  stroke={MEL_TEAL}
                  strokeWidth={10}
                  strokeLinecap="round"
                  strokeDasharray={arcC}
                  initial={{ strokeDashoffset: arcC }}
                  animate={{ strokeDashoffset: arcOffset }}
                  transition={{ duration: 1.25, ease: 'easeOut' }}
                />
              </svg>
              <div className="absolute inset-0 flex items-center justify-center pt-3 pointer-events-none">
                <div className="text-3xl font-bold tracking-tight text-gray-900">
                  {overallPct == null ? '—' : (
                    <>
                      <AnimatedNumber value={safeOverall} />
                      <span className="text-lg text-gray-500">%</span>
                    </>
                  )}
                </div>
              </div>
            </div>
            <div className="mt-2 text-[10px] uppercase tracking-wider text-gray-500 font-semibold text-center">
              Programme progress
            </div>
          </div>
          <div className="text-sm text-gray-600 leading-snug max-w-[160px]">
            <p className="font-medium text-gray-800">EuFMD&apos;s Programme</p>
            <p className="mt-1">
              {targetsWithData} of {targetCount} indicators with reported results
            </p>
          </div>
        </motion.div>

        <div className="flex-1 flex flex-wrap items-start justify-start lg:justify-end gap-3">
          <motion.button
            type="button"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            onClick={() => onSelectFocus(null)}
            className={`self-center text-xs font-semibold px-3 py-1.5 rounded-full border transition-colors ${
              selectedFocusId == null
                ? 'bg-[#15736d] text-white border-[#15736d]'
                : 'bg-white text-[#15736d] border-[#15736d]/50 hover:bg-[#f0f7f6]'
            }`}
          >
            All focus areas
          </motion.button>
          {focusObjectives.map((fo, i) => (
            <div key={fo.id} className="relative">
              <RadialMeter
                pct={fo.avg_progress_pct}
                label={fo.name}
                active={selectedFocusId === fo.id}
                onClick={() => onSelectFocus(selectedFocusId === fo.id ? null : fo.id)}
                delay={0.1 + i * 0.12}
              />
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default AchievementHud;
